// pms-core / domain — ARRHES: how they are paid, and the bank-transfer instructions.
//
// « Arrhes », not « acompte »: the word has a fiscal and a legal meaning (a sum
// paid at booking that either side may forfeit; VAT is not due on it when taken,
// unlike an acompte), so every reader says arrhes (operator, 2026-10-31).
//
// Two ways to pay them on a desk booking (PMS-PHONE-BOOKING-BRIEF §4.6):
//
//   vad       a secure card link — the bank's VAD today, Channex's vault when it lands
//   transfer  a bank transfer to the property's account, whose instructions are a
//             SETTING of the property (pms_property_bank), rendered into the
//             confirmation mail, never typed twice
//
// Pure: no clock, no database. The IBAN is public information (printed on every
// invoice), but it is data of the property, so it lives in the database where the
// operator edits it, not in the code.

import type { Cents, IsoDate } from './types.js';
import { termsAmount, termsDate, type TermsLang } from './cancellationPolicy.js';

export type ArrhesMethod = 'vad' | 'transfer';

export const ARRHES_METHODS: readonly ArrhesMethod[] = ['vad', 'transfer'];

export const ARRHES_METHOD_LABELS_FR: Record<ArrhesMethod, string> = {
  vad: 'VAD (lien carte)',
  transfer: 'Virement',
};

/** A property's bank-transfer instructions (`pms_property_bank`). */
export interface BankTransferDetails {
  propertyCode: string;
  /** « LE MONTAIGU HOTELS ET RESORTS ». */
  accountHolder: string;
  /** Compact, upper case, no spaces — display with `formatIban`. */
  iban: string;
  /** « SOGEFRPP » — 8 or 11 characters. */
  bic: string;
  /** Days the guest has to pay by transfer, from the booking (never past the arrival). */
  dueDays: number;
}

export const DEFAULT_TRANSFER_DUE_DAYS = 7;

// ── IBAN / BIC ───────────────────────────────────────────────────────────────

/** Upper case, spaces and dashes out. */
export const normalizeIban = (raw: string): string => raw.replace(/[\s-]+/g, '').toUpperCase();

/** « FR76 3000 3020 5700 0200 5605 114 » — groups of four, as banks print it. */
export const formatIban = (raw: string): string => normalizeIban(raw).replace(/(.{4})(?=.)/g, '$1 ');

/** ISO 13616 mod-97: the first four characters move to the end, letters become 10–35. */
function mod97(iban: string): number {
  const moved = iban.slice(4) + iban.slice(0, 4);
  let rem = 0;
  for (const ch of moved) {
    const v = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of v) rem = (rem * 10 + Number(d)) % 97;
  }
  return rem;
}

/** The IBAN's problem as one French sentence, or null. French IBANs must be 27 characters. */
export function ibanProblem(raw: string): string | null {
  const iban = normalizeIban(raw);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return 'IBAN : deux lettres, deux chiffres, puis le numéro de compte.';
  if (iban.startsWith('FR') && iban.length !== 27) return 'IBAN français : 27 caractères.';
  if (mod97(iban) !== 1) return 'IBAN : la clé de contrôle ne correspond pas — une faute de frappe ?';
  return null;
}

export const normalizeBic = (raw: string): string => raw.replace(/\s+/g, '').toUpperCase();

export function bicProblem(raw: string): string | null {
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(normalizeBic(raw)) ? null : 'BIC / SWIFT : 8 ou 11 caractères (par ex. SOGEFRPP).';
}

export function bankTransferProblem(b: Omit<BankTransferDetails, 'propertyCode'>): string | null {
  if (b.accountHolder.trim() === '') return 'Titulaire du compte requis.';
  if (b.accountHolder.length > 120) return 'Titulaire du compte : 120 caractères au plus.';
  const iban = ibanProblem(b.iban);
  if (iban !== null) return iban;
  const bic = bicProblem(b.bic);
  if (bic !== null) return bic;
  if (!Number.isInteger(b.dueDays) || b.dueDays < 1 || b.dueDays > 60) return 'Délai de virement : de 1 à 60 jours.';
  return null;
}

// ── The deadline ─────────────────────────────────────────────────────────────

const addDays = (d: IsoDate, n: number): IsoDate => {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};

/**
 * The day by which the transfer should have arrived: `days` after the booking,
 * but never later than the arrival (a stay booked for tomorrow is due tomorrow),
 * and never before the booking day.
 */
export function transferDueOn(bookedOn: IsoDate, arrival: IsoDate, days: number): IsoDate {
  const due = addDays(bookedOn, days);
  const capped = due > arrival ? arrival : due;
  return capped < bookedOn ? bookedOn : capped;
}

/** Why a typed deadline is unacceptable, or null: not before the booking day, not after the arrival. */
export function transferDueProblem(dueOn: IsoDate, bookedOn: IsoDate, arrival: IsoDate): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dueOn)) return 'Échéance du virement : une date.';
  if (dueOn < bookedOn) return 'Échéance du virement : pas avant aujourd’hui.';
  if (dueOn > arrival) return 'Échéance du virement : au plus tard le jour d’arrivée.';
  return null;
}

/** A transfer still expected and past its date — what the desk chases. */
export const transferOverdue = (dueOn: IsoDate | null, today: IsoDate): boolean => dueOn !== null && dueOn < today;

// ── The text (the same lines in the script, the mail and the screen) ─────────

export interface TransferInstructionsInput {
  details: Pick<BankTransferDetails, 'accountHolder' | 'iban' | 'bic'>;
  /** The arrhes asked, in cents. 0 or less: no amount is named. */
  amountCents: Cents;
  dueOn: IsoDate;
  /** What the guest writes on the transfer so the desk can match it — the booking reference. */
  reference: string;
}

interface TransferPhrases {
  heading: (amount: string | null, due: string) => string;
  holder: string;
  iban: string;
  bic: string;
  reference: string;
  after: string;
}

const TRANSFER_PHRASES: Record<'fr' | 'en', TransferPhrases> = {
  fr: {
    heading: (a, d) => `Règlement des arrhes par virement bancaire${a ? ` (${a})` : ''}, à effectuer avant le ${d} inclus :`,
    holder: 'Bénéficiaire',
    iban: 'IBAN',
    bic: 'BIC / SWIFT',
    reference: 'Référence à indiquer sur le virement',
    after: 'Sans réception du virement à cette date, la réception vous contactera.',
  },
  en: {
    heading: (a, d) => `Arrhes (deposit) by bank transfer${a ? ` (${a})` : ''}, to be paid by ${d} inclusive:`,
    holder: 'Account holder',
    iban: 'IBAN',
    bic: 'BIC / SWIFT',
    reference: 'Reference to quote on the transfer',
    after: 'If the transfer has not reached us by then, reception will contact you.',
  },
};

/** The transfer block as lines — « • » bullets are the caller's choice, so these are plain. */
export function transferInstructionLines(input: TransferInstructionsInput, lang: TermsLang): string[] {
  const p = TRANSFER_PHRASES[lang === 'en' ? 'en' : 'fr'];
  // The renderer knows fr / en / es dates; the mail speaks French else English.
  const l: TermsLang = lang === 'en' ? 'en' : 'fr';
  const amount = input.amountCents > 0 ? termsAmount(input.amountCents, l) : null;
  return [
    p.heading(amount, termsDate(input.dueOn, l)),
    `${p.holder} : ${input.details.accountHolder.trim()}`,
    `${p.iban} : ${formatIban(input.details.iban)}`,
    `${p.bic} : ${normalizeBic(input.details.bic)}`,
    `${p.reference} : ${input.reference}`,
    p.after,
  ];
}
